import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useFormContext } from "react-hook-form";

const Communications = ({
    selectedCompany,
    fetchCompany
}) => {

    const {
        register,
        formState: { isDirty },
        setValue,
        reset,
        watch
    } = useFormContext();

    const [isLoading, setIsLoading] = useState(false);
    const [requireEmployeeID, setRequireEmployeeID] = useState(!!selectedCompany?.require_employee_id)
    const [saving, setSaving] = useState(false);
    const [operations_email, sms_functionality, email_functionality] = watch(['operations_email', 'sms_functionality', 'email_functionality']);

    useEffect(() => {
        register('operations_email');
        register('sms_functionality');
        register('email_functionality');
    }, [register]);

    useEffect(() => {
        setRequireEmployeeID(!!selectedCompany?.require_employee_id);
    }, [selectedCompany?.id, selectedCompany?.require_employee_id]);

    // Save changes
    const updateCompany = async () => {
        setIsLoading(true)
        try {
            await axiosInstance(`/companies/${selectedCompany.id}`, {
                method: 'PATCH',
                data: {
                    company: {
                        operations_email
                    }
                },
                headers: { Accept: 'application/json' },
            });

            await fetchCompany();
        } catch (error) {
            console.error("Couldn't update company: ", error);
        }
        finally {
            setIsLoading(false)
        }
    };

    const handleChangeFunctionality = async (featureType) => {
        
        try {
            await axiosInstance({
                method: 'GET',
                url: `/companies/${selectedCompany.id}/features/${featureType}`,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            await fetchCompany();
        } catch (error) {
            console.error(`Error updating ${featureType}`, error);
        }
    };

    const handleRequireEmployeeID = async (nextValue) => {
        setSaving(true);
        const prev = requireEmployeeID;
        setRequireEmployeeID(nextValue);

        try {
            await axiosInstance.put(
            `/companies/${selectedCompany.id}/require_employee`,
            { require_employee_id: nextValue },
            { headers: { Accept: 'application/json', 'Content-Type': 'application/json' } }
            );

        } catch (err) {
            setRequireEmployeeID(prev);
            console.error('Error updating require_employee_id', err);
        } finally {
            setSaving(false);
        }
    };

    // Revert unsaved changes
    const handleRevertChanges = () => {
        reset();
    };

    return (
        <div className="w-full p-4 flex flex-col">

            {/* Communications section */}
            <div className="communications flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Communications</h3>
                        <p className="text-sm text-gray-500">Communication methods</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4 flex flex-col pr-5">
                    <div className="w-full flex flex-wrap">
                        {/* SMS Notifications */}
                        <div className="w-full md:w-1/6 flex flex-col pr-2">
                            <div className="flex flex-col">
                                <label className="label font-medium text-gray-900" style={{ whiteSpace: 'pre-wrap' }}>SMS Notifications</label>
                                <input
                                    type="checkbox"
                                    checked={sms_functionality}
                                    style={{
                                        transform: 'scale(3)',
                                        width: '15px',
                                        height: '15px',
                                        padding: '0',
                                        margin: '0',
                                        marginLeft: '15px',
                                        marginTop: '10px'
                                    }}
                                    onChange={() => handleChangeFunctionality("sms")}
                                />
                            </div>
                        </div>

                        {/* Email Notifications */}
                        <div className="w-full md:w-1/6 flex flex-col pr-2">
                            <div className="flex flex-col">
                                <label className="label font-medium text-gray-900" style={{ whiteSpace: 'pre-wrap' }}>Email Notifications</label>
                                <input
                                    type="checkbox"
                                    checked={email_functionality}
                                    style={{
                                        transform: 'scale(3)',
                                        width: '15px',
                                        height: '15px',
                                        padding: '0',
                                        margin: '0',
                                        marginLeft: '15px',
                                        marginTop: '10px'
                                    }}
                                    onChange={() => handleChangeFunctionality("emails")}
                                />
                            </div>
                        </div>

                        {/* Push Notifications */}
                        <div className="w-full md:w-1/6 flex flex-col pr-2">
                            <div className="flex flex-col">
                                <label className="label font-medium text-gray-900" style={{ whiteSpace: 'pre-wrap' }}>Push Notifications</label>
                                <input
                                    type="checkbox"
                                    disabled
                                    style={{
                                        transform: 'scale(3)',
                                        width: '15px',
                                        height: '15px',
                                        padding: '0',
                                        margin: '0',
                                        marginLeft: '15px',
                                        marginTop: '10px',
                                        backgroundColor: '#e0e0e0',
                                        cursor: 'not-allowed'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Validate Employee ID */}
                        <div className="w-full md:w-1/6 flex flex-col pr-2">
                            <div className="flex flex-col">
                                <label className="label font-medium text-gray-900" style={{ whiteSpace: 'pre-wrap' }}>Requires Employee ID</label>
                                <input
                                    type="checkbox"
                                    checked={!!requireEmployeeID}
                                    disabled={saving || !selectedCompany}
                                    style={{
                                        transform: 'scale(3)',
                                        width: '15px',
                                        height: '15px',
                                        padding: 0,
                                        margin: 0,
                                        marginLeft: '15px',
                                        marginTop: '10px'
                                    }}
                                    onChange={(e) => handleRequireEmployeeID(e.target.checked)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="w-full flex flex-col mt-8">
                        <label className="label font-medium text-gray-900">Operations Email</label>
                        <input
                            type="text"
                            value={operations_email}
                            onChange={(e) => setValue('operations_email', e.target.value, { shouldDirty: true })}
                            style={{
                                width: '100%',
                                padding: '12px',
                                border: '1px solid #ccc',
                                borderRadius: '4px'
                            }}
                            className="input border text-sm py-1 rounded-lg w-full md:w-3/4 lg:w-5/6 border-gray-400 text-gray-900 px-2"
                        />
                    </div>
                </div>
            </div>

            <hr className="border border-gray-200 my-5" />

            {/* Notification Center section */}
            <div className="notification_center">
                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Notification Center</h3>
                        <p className="text-sm text-gray-500">Sub details</p>
                    </div>
                </div>
                <div className="w-full md:w-1/3 flex flex-col mx-auto border-8 border-gray-400 p-4 h-32 text-xl text-gray-500 flex items-center justify-center" style={{ whiteSpace: 'pre-wrap' }}>
                    <p>Notification Center is coming soon!</p>
                </div>
            </div>

            <hr className="border border-gray-200 my-3 mb-16" />

            {/* Save and revert buttons */}
            <div className="relative pt-8">
                <div className="save_revert_buttons absolute bottom-0 right-0 flex space-x-3">
                    <button
                        className={`button p-2 text-white rounded text-sm flex align-center justify-center relative cursor-pointer ${
                            isLoading ? "bg-gray-400" : "button"
                        }`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={() => updateCompany()}
                        disabled={isLoading}
                    >
                        {isLoading ? "Saving..." : "Save"}
                    </button>
                    <button
                        className={`p-2 text-white rounded text-sm flex items-center justify-center relative cursor-pointer ${
                            isDirty ? "bg-gray-400" : "bg-gray-500 hover:bg-gray-600"
                        }`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={() => handleRevertChanges()}
                        disabled={isDirty == false}
                    >
                        Revert
                    </button>
                </div>
            </div>
        </div>
    );

}

export default Communications;